import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Copy, Check, Image as ImageIcon, FileText,
  Braces, ExternalLink, Terminal, Sparkles, Loader2, Download,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PublicNavbar from '../components/PublicNavbar';
import FooterBackground from '../components/FooterBackground';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

// ─── Format descriptors ────────────────────────────────────────────────────
const FORMATS = [
  {
    id: 'svg',
    suffix: '.svg',
    label: 'OG Image',
    subtitle: 'Crawler-friendly 1200×630 share card',
    icon: ImageIcon,
    accent: 'from-amber-500/20 to-orange-500/10',
    border: 'border-amber-400/30',
    tag: 'image/svg+xml',
    tagColor: 'text-amber-200 bg-amber-500/10 border-amber-400/30',
    useCase: 'Paste in <img> tags, LinkedIn unfurls, Slack previews',
    previewType: 'image',
  },
  {
    id: 'txt',
    suffix: '.txt',
    label: 'Caption',
    subtitle: 'Plain-text LinkedIn / X draft post',
    icon: FileText,
    accent: 'from-emerald-500/20 to-emerald-600/10',
    border: 'border-emerald-400/30',
    tag: 'text/plain',
    tagColor: 'text-emerald-200 bg-emerald-500/10 border-emerald-400/30',
    useCase: 'Ready-to-paste copy for share-kits, email newsletters',
    previewType: 'text',
  },
  {
    id: 'json',
    suffix: '.json',
    label: 'Bundle',
    subtitle: 'One-shot payload for widget embeds',
    icon: Braces,
    accent: 'from-emerald-500/20 to-emerald-600/10',
    border: 'border-emerald-400/30',
    tag: 'application/json',
    tagColor: 'text-emerald-200 bg-emerald-500/10 border-emerald-400/30',
    useCase: 'Dev.to / Hashnode widgets, 3rd-party dashboards',
    previewType: 'json',
  },
  {
    id: 'html',
    suffix: '',
    label: 'Landing',
    subtitle: 'OG-tagged HTML page for link unfurls',
    icon: ExternalLink,
    accent: 'from-emerald-500/20 to-emerald-600/10',
    border: 'border-emerald-400/30',
    tag: 'text/html',
    tagColor: 'text-emerald-200 bg-emerald-500/10 border-emerald-400/30',
    useCase: 'Put THIS url in your LinkedIn / X post for unfurls',
    previewType: 'none',
  },
];

// ─── Copy button with success state ────────────────────────────────────────
const CopyButton = ({ text, testId }) => {
  const [copied, setCopied] = useState(false);
  const onClick = useCallback(() => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* no-op */
    }
  }, [text]);
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-semibold transition-all ${
        copied
          ? 'border-emerald-400/50 bg-emerald-500/15 text-emerald-200'
          : 'border-slate-600 bg-slate-800 text-slate-300 hover:border-slate-500 hover:text-white'
      }`}
      aria-label={copied ? 'Copied' : 'Copy'}
    >
      {copied ? (
        <>
          <Check className="w-3 h-3" /> Copied
        </>
      ) : (
        <>
          <Copy className="w-3 h-3" /> Copy
        </>
      )}
    </button>
  );
};

// ─── Format card ───────────────────────────────────────────────────────────
const FormatCard = ({ format, pct, bundle }) => {
  const url = `${BASE}/api/og/milestone/${pct}${format.suffix}`;
  const curl = `curl -s "${url}"`;
  const Icon = format.icon;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(format.previewType !== 'none');

  useEffect(() => {
    if (format.previewType === 'none') return;
    if (format.previewType === 'image') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (format.previewType === 'json') {
          const r = await axios.get(url);
          if (cancelled) return;
          // Defensive: r.data may be an object (axios auto-parsed) OR a
          // pre-encoded string depending on response headers. Normalize.
          const obj =
            typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
          setPreview(JSON.stringify(obj, null, 2));
        } else {
          const r = await axios.get(url, { responseType: 'text' });
          if (cancelled) return;
          setPreview(typeof r.data === 'string' ? r.data : String(r.data));
        }
      } catch {
        if (!cancelled) setPreview('// Error loading preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, format.previewType]);

  return (
    <Card
      className={`bg-slate-900 text-slate-100 border ${format.border} backdrop-blur-sm overflow-hidden group hover:border-opacity-60 transition-all`}
      data-testid={`api-format-card-${format.id}`}
    >
      {/* Top gradient accent */}
      <div
        className={`bg-gradient-to-br ${format.accent} px-5 py-4 border-b ${format.border}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`shrink-0 w-9 h-9 rounded-lg bg-slate-950 border ${format.border} flex items-center justify-center`}
            >
              <Icon className="w-4 h-4 text-slate-100" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-mono text-sm font-bold text-white">
                  /milestone/{pct}
                  {format.suffix || ''}
                </span>
              </div>
              <div className="text-[11px] text-slate-400/80">
                {format.subtitle}
              </div>
            </div>
          </div>
          <Badge
            className={`shrink-0 border ${format.tagColor} font-mono text-[11px] uppercase tracking-wider`}
          >
            {format.tag}
          </Badge>
        </div>
      </div>

      <CardContent className="p-0">
        {/* URL row */}
        <div className="px-5 py-3 bg-slate-950 border-b border-slate-800 flex items-center justify-between gap-3">
          <code className="font-mono text-[11px] text-slate-400 truncate flex-1">
            {url}
          </code>
          <CopyButton text={url} testId={`api-copy-url-${format.id}`} />
        </div>

        {/* Preview */}
        <div className="px-5 py-4 bg-slate-950 min-h-[180px] flex items-stretch">
          {loading && (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}
          {!loading && format.previewType === 'image' && (
            <img
              src={url}
              alt={`${pct}% milestone OG card`}
              className="w-full max-h-[260px] object-contain rounded-md border border-slate-800 bg-slate-950"
              loading="lazy"
            />
          )}
          {!loading && format.previewType === 'text' && (
            <pre className="w-full text-[11px] text-slate-400 font-mono leading-relaxed whitespace-pre-wrap break-words max-h-[260px] overflow-y-auto">
              {preview}
            </pre>
          )}
          {!loading && format.previewType === 'json' && (
            <pre className="w-full text-[11px] text-slate-400 font-mono leading-relaxed whitespace-pre overflow-auto max-h-[260px]">
              {preview}
            </pre>
          )}
          {format.previewType === 'none' && (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 py-6">
              <div
                className={`w-12 h-12 rounded-full bg-gradient-to-br ${format.accent} border ${format.border} flex items-center justify-center`}
              >
                <ExternalLink className="w-5 h-5 text-cyan-200" />
              </div>
              <div className="text-[12px] text-slate-400 max-w-[260px]">
                This URL is what you put in your LinkedIn / X post. Social
                crawlers unfurl it into the OG image above.
              </div>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] text-cyan-200 hover:text-cyan-100 font-semibold"
                data-testid={`api-open-html-${format.id}`}
              >
                Open in new tab <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>

        {/* Curl snippet */}
        <div className="px-5 py-3 bg-slate-950 border-t border-slate-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Terminal className="w-3 h-3 text-slate-400 shrink-0" />
            <code className="font-mono text-[11px] text-slate-400 truncate">
              {curl}
            </code>
          </div>
          <CopyButton text={curl} testId={`api-copy-curl-${format.id}`} />
        </div>

        {/* Use case footer */}
        <div className="px-5 py-2.5 bg-gradient-to-r from-slate-950/40 to-transparent border-t border-slate-800/50 text-slate-100">
          <div className="text-[11px] text-slate-400 flex items-start gap-1.5">
            <Sparkles className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
            <span className="italic">{format.useCase}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────
const BuildApiPage = () => {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pct, setPct] = useState(45);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/og/milestone/latest.json`);
        if (cancelled) return;
        setBundle(r.data);
        if (r.data?.milestone_pct) setPct(r.data.milestone_pct);
      } catch {
        /* silent fallback to 45 */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Available gates — only those that have ever been announced OR are
  // valid forward-looking gates (for live_draft previews).
  const GATES = [35, 40, 45, 50, 55, 60, 65, 70, 75];

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      data-testid="build-api-page"
    >
      <PublicNavbar />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-slate-800/70">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 pt-12 pb-10">
          <Link
            to="/build/momentum"
            className="inline-flex items-center gap-2 text-[11px] text-slate-400 hover:text-cyan-300 transition-colors mb-8"
            data-testid="api-back-to-momentum"
          >
            <ArrowLeft className="w-3 h-3" /> Back to momentum
          </Link>

          <div className="flex items-center gap-2 mb-4">
            <div
              className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"
              aria-hidden
            />
            <span className="text-[11px] font-bold tracking-[0.22em] text-emerald-300 uppercase">
              Public API · No auth · Built in the open
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 leading-tight">
            The milestone share API,
            <br />
            <span className="text-emerald-400">
              in four flavors.
            </span>
          </h1>

          <p className="text-base text-slate-400 max-w-2xl leading-relaxed mb-8">
            Every time we cross a 5% monolith-reduction gate, we publish a
            crawler-friendly OG card, a plain-text caption, a full JSON
            bundle, and an HTML landing page. One URL pattern. Four formats.
            Byte-for-byte parity across all four.
          </p>

          {/* Gate picker */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 mr-1">
              Preview gate:
            </span>
            {GATES.map((g) => {
              const isActive = g === pct;
              const crossed = bundle && g <= (bundle.milestone_pct || 0);
              return (
                <button
                  key={g}
                  onClick={() => setPct(g)}
                  data-testid={`api-gate-${g}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold font-mono border transition-all ${
                    isActive
                      ? 'bg-emerald-500/20 border-emerald-400/60 text-cyan-200 shadow-lg shadow-cyan-500/20'
                      : crossed
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-200 hover:border-amber-400/60'
                      : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                  }`}
                  aria-pressed={isActive}
                >
                  {g}%{crossed && !isActive && <span className="ml-1">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Status strip */}
      {!loading && bundle && (
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3 rounded-xl border border-slate-800 bg-slate-900 text-[11px] text-slate-400"
            data-testid="api-status-strip"
          >
            <div>
              <span className="text-slate-400">Status:</span>{' '}
              <span
                className={`font-bold ${
                  pct <= (bundle.milestone_pct || 0)
                    ? 'text-emerald-300'
                    : 'text-amber-300'
                }`}
              >
                {pct <= (bundle.milestone_pct || 0)
                  ? 'crossed'
                  : 'live_draft'}
              </span>
            </div>
            <div>
              <span className="text-slate-400">server.py now:</span>{' '}
              <span className="font-mono font-bold text-slate-300 tabular-nums">
                {bundle.stats?.current_lines?.toLocaleString() || '—'} lines
              </span>
            </div>
            <div>
              <span className="text-slate-400">Reduced:</span>{' '}
              <span className="font-mono font-bold text-cyan-300 tabular-nums">
                -{bundle.stats?.lines_reduced?.toLocaleString() || '—'}
              </span>{' '}
              <span className="text-slate-400">
                ({bundle.stats?.reduction_pct || 0}%)
              </span>
            </div>
            <div>
              <span className="text-slate-400">Modules:</span>{' '}
              <span className="font-mono font-bold text-emerald-300 tabular-nums">
                {bundle.stats?.modules_extracted || 0}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Format cards grid */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-5">
          {FORMATS.map((f) => (
            <FormatCard key={f.id} format={f} pct={pct} bundle={bundle} />
          ))}
        </div>
      </div>

      {/* OpenAPI / Swagger callout — Iter76e */}
      <div className="max-w-6xl mx-auto px-6 pb-10">
        <Card
          className="bg-gradient-to-br from-sky-500/[0.06] via-indigo-500/[0.04] to-slate-900/30 border-sky-400/20 overflow-hidden text-slate-100"
          data-testid="api-openapi-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/20 to-indigo-500/20 border border-sky-400/30 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-sky-300"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-emerald-500/15 text-sky-200 border-sky-400/30 font-mono text-[11px] uppercase tracking-wider">
                    OpenAPI 3.1
                  </Badge>
                  <span className="text-[11px] font-bold tracking-[0.18em] text-sky-300 uppercase">
                    Self-describing
                  </span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                  Import the API in one click.
                </h2>
                <p className="text-sm text-slate-400 leading-relaxed max-w-2xl">
                  A full OpenAPI 3.1 spec describes all 8 endpoints plus the{' '}
                  <code className="font-mono text-sky-200">MilestoneBundle</code>{' '}
                  response schema. Import into Postman / Bruno / Insomnia, or
                  view it live in Swagger UI.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:shrink-0">
                <a
                  href={`${API}/og/milestone/openapi.json`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download="cartieai-milestone-api-openapi.json"
                  data-testid="api-openapi-download"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-sky-400/50 bg-emerald-500/15 hover:bg-emerald-500/30 text-sky-100 text-[12px] font-bold transition-all"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download spec
                </a>
                <a
                  href={`https://petstore.swagger.io/?url=${encodeURIComponent(
                    `${BASE}/api/og/milestone/openapi.json`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="api-openapi-swagger-ui"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-700 hover:border-slate-500 bg-slate-900 hover:bg-slate-800 text-slate-200 text-[12px] font-bold transition-all"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open in Swagger UI
                </a>
              </div>
            </div>

            {/* URL strip */}
            <div className="mt-5 flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-slate-950 text-slate-100 border border-slate-800/60">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Terminal className="w-3 h-3 text-slate-400 shrink-0" />
                <code className="font-mono text-[11px] text-slate-400 truncate">
                  GET {BASE}/api/og/milestone/openapi.json
                </code>
              </div>
              <CopyButton
                text={`${BASE}/api/og/milestone/openapi.json`}
                testId="api-copy-openapi-url"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Convention callout */}
      <div className="max-w-6xl mx-auto px-6 pb-16">
        <Card className="bg-slate-950 bg-gradient-to-br from-slate-900/80 to-slate-950 border-slate-800 text-slate-100">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
              <span className="text-[11px] font-bold tracking-[0.18em] text-cyan-300 uppercase">
                Convention
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
              One URL pattern. Any format you need.
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed mb-5 max-w-2xl">
              Every milestone URL shares the same stem. Swap the suffix to
              pick a format. Also works with <code className="font-mono text-cyan-300">latest</code>{' '}
              instead of a numeric gate — we resolve it to the most recently
              crossed milestone.
            </p>
            <div className="space-y-2 font-mono text-[12px]">
              {['svg', 'txt', 'json', ''].map((suf) => (
                <div
                  key={suf || 'html'}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-slate-950 text-slate-100 border border-slate-800/60 hover:border-slate-700 transition-colors"
                >
                  <Badge className="bg-slate-800 border border-slate-700 text-slate-300 font-mono text-[11px] uppercase">
                    GET
                  </Badge>
                  <code className="text-slate-400 truncate flex-1">
                    /api/og/milestone/{'{pct|latest}'}
                    {suf ? `.${suf}` : ''}
                  </code>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <FooterBackground />
    </div>
  );
};

export default BuildApiPage;
